import type { ServiceDefinition } from '../types/content';

export const services: ServiceDefinition[] = [
  {
    slug: 'retained-executive-search',
    name: 'Retained Executive Search',
    shortDescription:
      'A commissioned, research-led search for a single senior appointment, conducted on an exclusive basis.',
    audience:
      'Boards, chief executives and chief human resources officers appointing a leader whose performance materially changes the trajectory of the organization.',
    organizationalChallenge:
      'The appointment matters enough that a contingent, volume-driven approach is inadequate. The relevant population is small, largely employed, and will not respond to advertising. The organization needs an evidenced view of who exists, not a stack of applications.',
    methodology: [
      {
        step: 'Specification and calibration',
        detail:
          'Structured conversations with the board, the hiring executive and adjacent stakeholders to establish the mandate, the success measures for the first eighteen months, and the areas where the organization is genuinely willing to trade.',
      },
      {
        step: 'Market research and mapping',
        detail:
          'Systematic identification of the addressable population by function, sector adjacency and organizational scale, documented so the client can see the boundary of the search rather than only its output.',
      },
      {
        step: 'Discreet approach',
        detail:
          'Individual, named approaches made by the mandate owner. Candidate interest is treated as confidential to the search until the candidate authorizes otherwise.',
      },
      {
        step: 'Assessment against the specification',
        detail:
          'Competency-based interviews evidencing scope, decision quality and leadership behaviour, calibrated against the agreed measures rather than against other candidates.',
      },
      {
        step: 'Shortlist presentation',
        detail:
          'A written submission for each candidate presented only after that candidate has authorized presentation to the named client.',
      },
      {
        step: 'Offer, close and integration',
        detail:
          'Support through reference checking, offer construction and the first ninety days, with a documented handover to the organization.',
      },
    ],
    deliverables: [
      'Position specification agreed in writing before search commences',
      'Documented market map showing the addressable population and search boundary',
      'Progress reporting at agreed intervals for the duration of the engagement',
      'Written assessment for each presented candidate',
      'Reference report on the preferred candidate, undertaken with consent',
      'Post-appointment review at an agreed interval',
    ],
    confidentialitySafeguards: [
      'Candidate identity is never disclosed to a client without that candidate’s recorded, mandate-specific authorization',
      'Client identity may be withheld from the market at the client’s instruction, recorded against the mandate',
      'Candidate documents are held in private storage and shared through authenticated portal access only',
      'Access to mandate records is restricted to the assigned search team',
    ],
    typicalTimeline: 'Specification to shortlist typically runs several weeks; timelines are agreed per mandate.',
    isBoardPractice: false,
  },
  {
    slug: 'c-suite-recruitment',
    name: 'C-Suite Recruitment',
    shortDescription:
      'Appointments to the executive committee, where the brief is as much about the shape of the role as the person in it.',
    audience:
      'Chief executives and boards appointing a chief financial, operating, technology, commercial, legal, people or medical officer.',
    organizationalChallenge:
      'Executive-committee roles are rarely like-for-like replacements. The scope has usually shifted since the last appointment, and the organization needs help deciding what the role now is before deciding who should hold it.',
    methodology: [
      {
        step: 'Role definition',
        detail:
          'Testing the assumed scope against the strategy, the peer set and the realistic external market before the specification is fixed.',
      },
      {
        step: 'Stakeholder calibration',
        detail:
          'Alignment across the chief executive, the relevant board committee and the executive peers who must work with the appointee.',
      },
      {
        step: 'Targeted research',
        detail: 'Mapping of comparable scope across direct and adjacent sectors, including candidates one step from the seat.',
      },
      {
        step: 'Assessment and shortlist',
        detail: 'Evidence-based assessment of functional depth, enterprise contribution and executive-committee behaviour.',
      },
      {
        step: 'Committee integration',
        detail: 'Structured onboarding into the executive committee, including a first-ninety-days framework.',
      },
    ],
    deliverables: [
      'Calibrated role specification, including what the role is not',
      'Comparative view of scope in the relevant peer set',
      'Written assessment for each presented candidate',
      'Executive-committee integration plan',
    ],
    confidentialitySafeguards: [
      'Incumbent-sensitive searches are conducted without market disclosure of the client',
      'Internal candidates are assessed on the same evidence base and informed of the process',
      'Mandate-specific authorization required before any external presentation',
    ],
    typicalTimeline: 'Agreed per mandate at the point of engagement.',
    isBoardPractice: false,
  },
  {
    slug: 'board-director-search',
    name: 'Board Director Search',
    shortDescription:
      'Non-executive and independent director appointments, including committee-chair and audit-qualified requirements.',
    audience:
      'Nomination committees, board chairs, and private-equity or founder-led boards professionalizing their governance.',
    organizationalChallenge:
      'Board composition is a matrix problem, not a shortlist problem. The committee needs to see the gap in skills, sector exposure and independence before it can sensibly consider individuals.',
    methodology: [
      {
        step: 'Board composition review',
        detail:
          'A skills and experience matrix across the current board, mapped against strategy, regulatory exposure and committee obligations.',
      },
      {
        step: 'Gap definition',
        detail: 'Written definition of the specific contribution the new director must make, agreed by the committee.',
      },
      {
        step: 'Research across constituencies',
        detail:
          'Identification of candidates including first-time directors, operating executives with committee-relevant depth, and sitting directors with capacity.',
      },
      {
        step: 'Independence and conflict review',
        detail: 'Documented review of independence, competing directorships, time commitment and potential conflicts.',
      },
      {
        step: 'Committee process',
        detail: 'Support through committee meetings, chair conversations and appointment documentation.',
      },
    ],
    deliverables: [
      'Board skills and experience matrix',
      'Written director specification agreed by the nomination committee',
      'Longlist with independence assessment',
      'Written assessment and conflict review for each presented candidate',
    ],
    confidentialitySafeguards: [
      'Board searches are conducted confidentially by default',
      'Director candidates’ interest is not disclosed to their current boards',
      'Committee papers are shared through authenticated access only',
    ],
    typicalTimeline: 'Agreed per mandate; board cycles usually set the pace.',
    isBoardPractice: true,
  },
  {
    slug: 'succession-planning',
    name: 'Succession Planning',
    shortDescription:
      'Building an evidenced internal and external succession picture for critical leadership seats before they become vacant.',
    audience: 'Boards, chief executives and chief human resources officers with concentration risk in one or more critical seats.',
    organizationalChallenge:
      'Succession is usually a document rather than a capability. Internal successors are named without evidence, external benchmarks do not exist, and the organization discovers the gap at the worst possible moment.',
    methodology: [
      { step: 'Critical-role identification', detail: 'Agreeing which seats carry genuine enterprise risk if vacated.' },
      { step: 'Internal readiness assessment', detail: 'Assessment of internal candidates against the future requirement, not the current one.' },
      { step: 'External benchmarking', detail: 'A confidential external map showing the depth and quality of the alternative market.' },
      { step: 'Development planning', detail: 'Specific, time-bounded development actions for each internal successor.' },
      { step: 'Annual refresh', detail: 'Re-testing the picture as strategy and personnel change.' },
    ],
    deliverables: [
      'Critical-role register with risk rating',
      'Individual readiness assessments with development actions',
      'Confidential external benchmark map per critical seat',
      'Board-ready succession summary',
    ],
    confidentialitySafeguards: [
      'Internal assessments are shared only with the agreed sponsor group',
      'External benchmark candidates are approached without reference to a specific vacancy',
      'No individual is told they are a designated successor without the client’s explicit instruction',
    ],
    typicalTimeline: 'Ordinarily a defined project followed by an annual refresh cycle.',
    isBoardPractice: false,
  },
  {
    slug: 'leadership-assessment',
    name: 'Leadership Assessment',
    shortDescription:
      'Structured, evidence-based assessment of individual leaders and leadership teams against a defined requirement.',
    audience:
      'Boards making an appointment decision, investors underwriting a management team, and chief executives assessing an inherited team.',
    organizationalChallenge:
      'Interview impressions and internal reputation are weak predictors. The decision-maker needs a defensible evidence base, and the individual deserves a process that is fair and explained.',
    methodology: [
      { step: 'Requirement definition', detail: 'A written competency and behaviour framework specific to the role and context.' },
      { step: 'Structured interview', detail: 'Competency-based interviewing evidencing decisions, scope and outcomes.' },
      { step: 'Referencing with consent', detail: 'Structured referencing undertaken only with the individual’s written consent.' },
      { step: 'Calibration', detail: 'Findings calibrated against the framework by two assessors, not one.' },
      { step: 'Feedback', detail: 'Written feedback offered to every assessed individual, whatever the outcome.' },
    ],
    deliverables: [
      'Assessment framework agreed in advance',
      'Individual assessment report against each competency',
      'Team-level view where a group is assessed',
      'Written feedback for each participant',
    ],
    confidentialitySafeguards: [
      'Assessment reports are released only to the agreed recipients',
      'Referencing occurs only with written consent',
      'No assessment outcome is used to make an adverse decision without human review and a recorded reason',
    ],
    typicalTimeline: 'Agreed per assignment.',
    isBoardPractice: false,
  },
  {
    slug: 'market-mapping',
    name: 'Market Mapping',
    shortDescription:
      'A documented view of the leadership population in a defined market, delivered without an appointment attached.',
    audience:
      'Organizations planning a future hire, entering a new market, or testing whether an internal candidate is genuinely competitive.',
    organizationalChallenge:
      'Decisions about structure, location and compensation are made without knowing who exists. A map converts assumption into evidence before money is committed.',
    methodology: [
      { step: 'Boundary definition', detail: 'Agreeing the functional, geographic and organizational-scale boundaries of the map.' },
      { step: 'Population identification', detail: 'Systematic identification and categorization of the addressable population.' },
      { step: 'Anonymized profiling', detail: 'Profiles built from public and research sources, anonymized unless an individual consents to be named.' },
      { step: 'Analysis', detail: 'Depth, mobility, compensation posture and diversity of the population, with the limits of the data stated.' },
    ],
    deliverables: [
      'Written market map with stated boundaries and method',
      'Anonymized population analysis by segment',
      'Observed compensation posture, with sample size disclosed',
      'Recommendations on feasibility and sequencing',
    ],
    confidentialitySafeguards: [
      'Individuals are not approached about a vacancy during a mapping exercise unless a mandate exists',
      'Named profiles appear only with the individual’s consent',
      'Client identity may be withheld throughout',
    ],
    typicalTimeline: 'A defined project with an agreed delivery date.',
    isBoardPractice: false,
  },
  {
    slug: 'talent-intelligence',
    name: 'Talent Intelligence',
    shortDescription:
      'Recurring research on leadership availability, movement and organizational design in the markets that matter to you.',
    audience: 'Chief human resources officers, heads of talent acquisition and strategy teams with an ongoing planning cycle.',
    organizationalChallenge:
      'Leadership market conditions change faster than annual planning. Without a research relationship, the organization only learns the market when it needs to hire.',
    methodology: [
      { step: 'Intelligence agenda', detail: 'Agreeing the questions worth answering each cycle and the decisions they support.' },
      { step: 'Standing research', detail: 'Continuous tracking of movement, structure and compensation posture in the defined markets.' },
      { step: 'Briefing cycle', detail: 'Regular written briefings and a working session with the relevant leadership group.' },
      { step: 'Ad-hoc enquiry', detail: 'A defined channel for specific questions between cycles.' },
    ],
    deliverables: [
      'Agreed intelligence agenda',
      'Periodic written briefings',
      'Working sessions with the sponsor group',
      'Response to defined ad-hoc enquiries',
    ],
    confidentialitySafeguards: [
      'Research is conducted without representing an unconfirmed vacancy',
      'Client identity withheld in market conversations unless instructed otherwise',
      'Individual data handled under our candidate privacy principles regardless of engagement type',
    ],
    typicalTimeline: 'An annual relationship with an agreed briefing cadence.',
    isBoardPractice: false,
  },
  {
    slug: 'confidential-replacement-search',
    name: 'Confidential Replacement Search',
    shortDescription:
      'A search conducted while the incumbent remains in seat, with no market disclosure of the client or the role.',
    audience: 'Boards and chief executives who must secure a successor before a departure is announced.',
    organizationalChallenge:
      'The organization cannot advertise, cannot brief its own recruiters, and cannot risk the incumbent, the market or its staff learning of the search prematurely. It still needs a credible shortlist.',
    methodology: [
      { step: 'Disclosure protocol', detail: 'A written protocol setting who inside the client knows, what candidates are told, and when disclosure occurs.' },
      { step: 'Blind approach', detail: 'Candidates approached against a described organization rather than a named one, with the reason for the restriction stated openly.' },
      { step: 'Staged disclosure', detail: 'Client identity released only to candidates who advance, and only before their profile is presented.' },
      { step: 'Controlled shortlist', detail: 'Presentation and interviews arranged outside normal channels to preserve confidentiality.' },
    ],
    deliverables: [
      'Written disclosure and communications protocol',
      'Documented market map',
      'Written assessment for each presented candidate',
      'Transition and announcement support',
    ],
    confidentialitySafeguards: [
      'Client identity withheld from the market until the agreed disclosure point',
      'Candidates always told that a restriction exists and why, never left to assume',
      'Confidentiality is never used to obscure whether the vacancy is confirmed',
      'Access to the mandate record limited to the named search team',
    ],
    typicalTimeline: 'Agreed per mandate; usually driven by the announcement date.',
    isBoardPractice: false,
  },
  {
    slug: 'private-equity-portfolio-leadership',
    name: 'Private-Equity Portfolio Leadership',
    shortDescription:
      'Leadership appointments across the deal cycle — diligence, the first hundred days, value creation and exit readiness.',
    audience: 'Deal teams, operating partners and portfolio-company boards.',
    organizationalChallenge:
      'Hold periods do not permit a slow appointment or a poor one. The requirement is a leader who has operated at the relevant stage of the cycle, not simply at the relevant scale.',
    methodology: [
      { step: 'Pre-deal management assessment', detail: 'Assessment of the incumbent team against the investment thesis during diligence.' },
      { step: 'Thesis-aligned specification', detail: 'A specification written against the value-creation plan and the hold period, not a generic role profile.' },
      { step: 'Stage-experienced research', detail: 'Research prioritizing leaders who have delivered at the relevant stage and ownership model.' },
      { step: 'Rapid, evidenced process', detail: 'A compressed process that retains written assessment and referencing discipline.' },
    ],
    deliverables: [
      'Pre-deal management assessment where commissioned',
      'Thesis-aligned specification',
      'Written assessment for each presented candidate',
      'Incentive and alignment observations for the board',
    ],
    confidentialitySafeguards: [
      'Pre-deal work conducted without disclosing the transaction',
      'Incumbent management assessed under a protocol agreed with the deal team',
      'Candidate authorization required before presentation to the fund or the portfolio company',
    ],
    typicalTimeline: 'Compressed to the transaction timetable where required.',
    isBoardPractice: false,
  },
  {
    slug: 'interim-executive-appointments',
    name: 'Interim Executive Appointments',
    shortDescription:
      'Experienced executives placed for a defined period to hold a seat, lead a programme or stabilize a function.',
    audience: 'Organizations facing an unplanned departure, a transformation programme or a gap during a permanent search.',
    organizationalChallenge:
      'The seat cannot stay empty, but a permanent appointment made under time pressure is the more expensive mistake. The organization needs credible capability quickly, on defined terms.',
    methodology: [
      { step: 'Scope and duration', detail: 'A written brief defining the outcome, the decision rights and the end date.' },
      { step: 'Availability-first research', detail: 'Identification of executives with the relevant scope who are genuinely available.' },
      { step: 'Fast, evidenced selection', detail: 'Assessment against the interim brief, with references taken on prior interim outcomes.' },
      { step: 'Handover planning', detail: 'A documented handover to the permanent appointee agreed at the outset.' },
    ],
    deliverables: [
      'Written interim brief with defined outcomes and end date',
      'Shortlist of available, assessed executives',
      'Agreed handover plan',
    ],
    confidentialitySafeguards: [
      'Interim engagements are recorded against the mandate with the same authorization requirements',
      'Client may remain undisclosed during approach where circumstances require',
    ],
    typicalTimeline: 'Ordinarily the fastest of our engagement types; agreed per assignment.',
    isBoardPractice: false,
  },
  {
    slug: 'executive-benchmarking',
    name: 'Executive Benchmarking',
    shortDescription:
      'Comparing an individual leader or a whole team against the external market for the same scope.',
    audience:
      'Boards testing an internal candidate, chief executives evaluating an inherited team, and remuneration committees seeking external context.',
    organizationalChallenge:
      'Internal reputation is not a market view. Before promoting, restructuring or repricing a role, the organization needs to know how the incumbent compares to the people it could otherwise appoint.',
    methodology: [
      { step: 'Comparator definition', detail: 'Agreeing the comparator set by scope, sector and organizational scale.' },
      { step: 'External research', detail: 'Anonymized profiling of comparable executives, with the sample size disclosed.' },
      { step: 'Structured comparison', detail: 'Comparison against a written framework rather than a general impression.' },
      { step: 'Findings and options', detail: 'A written view with the limits of the evidence stated plainly.' },
    ],
    deliverables: [
      'Defined comparator set',
      'Anonymized external benchmark analysis',
      'Written comparison against the agreed framework',
      'Options and recommendations',
    ],
    confidentialitySafeguards: [
      'Benchmark subjects are not told they are being compared to a named individual',
      'Findings released only to the agreed recipients',
      'Compensation observations always carry the sample size and are never presented as market fact',
    ],
    typicalTimeline: 'A defined project with an agreed delivery date.',
    isBoardPractice: false,
  },
  {
    slug: 'diversity-focused-leadership-search',
    name: 'Diversity-Focused Leadership Search',
    shortDescription:
      'Search conducted with deliberate breadth in the addressable population, and with an assessment process built to reduce bias.',
    audience:
      'Boards and executives who have concluded that their existing pipelines reproduce the composition of their current leadership.',
    organizationalChallenge:
      'Narrow research produces narrow slates. Widening the population only works if the assessment process is also rebuilt, and if the organization is honest about the conditions the appointee will actually join.',
    methodology: [
      { step: 'Population breadth', detail: 'Research boundaries deliberately widened across sector adjacency, organizational scale and non-linear career paths.' },
      { step: 'Specification review', detail: 'Testing every requirement for whether it is genuinely necessary or merely conventional.' },
      { step: 'Structured, calibrated assessment', detail: 'Identical structured interviews, two assessors, and calibration against the framework rather than against each other.' },
      { step: 'Process transparency', detail: 'Documented slate composition at each stage, reported to the client without individual attribution.' },
      { step: 'Inclusion conditions', detail: 'An honest conversation about the conditions the appointee will join, and what would need to change.' },
    ],
    deliverables: [
      'Reviewed specification with unnecessary requirements removed',
      'Documented research boundary and population breadth',
      'Slate composition reporting at each stage',
      'Structured assessment for each presented candidate',
    ],
    confidentialitySafeguards: [
      'Demographic information is never required from a candidate and never used to make an assessment decision',
      'Any voluntary disclosure is held separately from the assessment record',
      'Slate reporting is aggregate and never identifies individuals',
    ],
    typicalTimeline: 'As retained executive search; the research phase is ordinarily longer by design.',
    isBoardPractice: false,
  },
];

export const getService = (slug: string): ServiceDefinition | undefined =>
  services.find((s) => s.slug === slug);
